import {
  createAction,
  type ActionCreator,
} from '@packages/utilities/create-action';
import {entries} from '@packages/utilities/key-map';
import type {DesignerActionFromCreator} from '@packages/systems/designer/flux';

/**
 * Union type of all actions created from a record of action creators
 */
export type ActionsUnion<T extends Record<string, ActionCreator<string, any>>> =
  {
    [K in keyof T]: DesignerActionFromCreator<T[K]>;
  }[keyof T];

/**
 * Helper to define an action with a type constant
 */
export function defineAction<T extends string>(type: T) {
  return <P = void>() => ({type, payload: undefined as unknown as P});
}

/**
 * Creates action creators and action type constants from a definition object.
 */
export function createActionsAndTypes<
  TDef extends Record<string, {type: string; payload?: unknown}>,
>(defs: TDef) {
  const creators: Record<string, unknown> = {};
  const actionTypes: Record<string, string> = {};

  for (const [key, {type}] of entries(defs)) {
    creators[key] = createAction(type);
    actionTypes[type] = type;
  }

  return {
    creators: creators as {
      [K in keyof TDef]: ActionCreator<
        TDef[K]['type'],
        TDef[K]['payload'] extends undefined ? void : TDef[K]['payload']
      >;
    },

    actionTypes: actionTypes as {
      [K in TDef[keyof TDef]['type']]: K;
    },
  };
}
